import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Banknote, Plus } from "lucide-react";
import { StatCard, StatusBadge, money } from "./DashWidgets";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { myPayouts, requestPayout } from "../lib/api";

const EMPTY = { amount: "", method: "bank", account_name: "", account_number: "", ifsc: "", upi_id: "", note: "" };

export const PayoutsPanel = () => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = () => myPayouts().then(setData).catch(() => setData({ earned: 0, requested: 0, available: 0, requests: [] }));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    const amt = Number(form.amount);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    setLoading(true);
    try {
      await requestPayout({ ...form, amount: amt });
      toast.success("Withdrawal request submitted!");
      setOpen(false); setForm(EMPTY); load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Request failed");
    } finally { setLoading(false); }
  };

  if (!data) return <Loader2 className="w-6 h-6 text-gold animate-spin" />;

  return (
    <div data-testid="payouts-panel">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <h2 className="font-heading text-2xl font-bold text-white flex items-center gap-2"><Banknote className="w-6 h-6 text-gold" /> Payouts</h2>
        <Button onClick={() => setOpen(true)} data-testid="request-payout-button" disabled={data.available <= 0}
          className="gold-gradient text-obsidian-900 font-bold rounded-full disabled:opacity-50">
          <Plus className="w-4 h-4 mr-1" /> Request Withdrawal
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <StatCard icon="Coins" label="Total Earned" value={money(data.earned)} testid="payout-earned" />
        <StatCard icon="Clock" label="Requested / Paid" value={money(data.requested)} testid="payout-requested" />
        <StatCard icon="Wallet" label="Available to Withdraw" value={money(data.available)} accent="green" testid="payout-available" />
      </div>

      <h3 className="font-semibold text-white mb-3">Withdrawal History</h3>
      {data.requests.length === 0 ? (
        <p className="text-slate-400 text-sm">No withdrawal requests yet. Available commissions can be withdrawn once approved by admin.</p>
      ) : (
        <div className="space-y-3">
          {data.requests.map((r) => (
            <div key={r.id} className="card-dark rounded-xl p-4 flex items-center justify-between flex-wrap gap-2" data-testid={`payout-row-${r.id}`}>
              <div>
                <div className="text-white font-mono-fin font-bold">{money(r.amount)}</div>
                <div className="text-slate-500 text-xs">{r.method === "upi" ? `UPI · ${r.upi_id}` : `Bank · ${r.account_number}`}</div>
              </div>
              <StatusBadge status={r.status} />
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="card-dark border-gold/30 max-w-md" data-testid="payout-modal">
          <DialogHeader><DialogTitle className="font-heading text-xl font-extrabold text-white">Request Withdrawal</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3.5">
            <div>
              <Label className="text-slate-300 text-sm mb-1.5 block">Amount (₹) · Available {money(data.available)}</Label>
              <Input data-testid="payout-amount" type="number" value={form.amount} onChange={(e) => set("amount", e.target.value)} className="bg-obsidian-900/60 border-white/10 h-11 text-white" />
            </div>
            <Select value={form.method} onValueChange={(v) => set("method", v)}>
              <SelectTrigger data-testid="payout-method" className="bg-obsidian-900/60 border-white/10 h-11 text-white"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-obsidian-700 border-white/10 text-white">
                <SelectItem value="bank" className="focus:bg-gold/20 focus:text-white">Bank Transfer</SelectItem>
                <SelectItem value="upi" className="focus:bg-gold/20 focus:text-white">UPI</SelectItem>
              </SelectContent>
            </Select>
            {form.method === "bank" ? (
              <>
                <Input data-testid="payout-acc-name" placeholder="Account Holder Name" value={form.account_name} onChange={(e) => set("account_name", e.target.value)} className="bg-obsidian-900/60 border-white/10 h-11 text-white" />
                <Input data-testid="payout-acc-number" placeholder="Account Number" value={form.account_number} onChange={(e) => set("account_number", e.target.value)} className="bg-obsidian-900/60 border-white/10 h-11 text-white" />
                <Input data-testid="payout-ifsc" placeholder="IFSC Code" value={form.ifsc} onChange={(e) => set("ifsc", e.target.value)} className="bg-obsidian-900/60 border-white/10 h-11 text-white" />
              </>
            ) : (
              <Input data-testid="payout-upi" placeholder="UPI ID (e.g. name@bank)" value={form.upi_id} onChange={(e) => set("upi_id", e.target.value)} className="bg-obsidian-900/60 border-white/10 h-11 text-white" />
            )}
            <Textarea data-testid="payout-note" placeholder="Note (optional)" value={form.note} onChange={(e) => set("note", e.target.value)} className="bg-obsidian-900/60 border-white/10 text-white" />
            <Button type="submit" disabled={loading} data-testid="payout-submit" className="w-full gold-gradient text-obsidian-900 font-bold h-11 rounded-full">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Submit Request"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
